import forOwn from 'lodash/forOwn';
import uniqueId from 'lodash/uniqueId';
import minBy from 'lodash/minBy';
import { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import invariant from 'invariant';
import { formUtils, incaAccessor } from '@cancercentrum/inca';
import { RowModel } from '../models/RowModel';
import { createRegvarModel } from '../models/RegvarModel/utils';
import { ValueDomainModel } from '../models/ValueDomainModel';
import { SubTableModel } from '../models/SubTableModel';
export function createDataRow(tableName, globalMetadata) {
    return formUtils.createDataRow(tableName, globalMetadata);
}
/**
 * Creates a Immutable view model for row. If globalMetadata and tableName is missing then it is assumed
 * row represents the top row, e.g. inca.form.data.
 *
 * @param {Object} row              - INCA row to convert.
 * @param {Object} [globalMetadata] - Metdata, e.g. inca.form.metadata.
 * @param {string} [tableName]      - Name of the table the row represents.
 * @returns {RowModel}
 */ export function createViewModel(row, globalMetadata, tableName) {
    invariant(row, 'row parameter is missing.');
    const metadata = globalMetadata || incaAccessor().form.metadata;
    const tn = tableName || minBy(Object.keys(metadata), (x)=>metadata[x].treeLevel);
    const tableMetadata = metadata[tn];
    const regvarsMap = {};
    const vdlistsMap = {};
    const subTablesMap = {};
    const { id, regvars, vdlists, subTables, _destroy, ...rowExtra } = row;
    forOwn(tableMetadata.regvars, (regvarMetadata, shortname)=>{
        if (!row.regvars[shortname]) {
            throw new Error(`Table metadata for "${tn}" contains a regvar "${shortname}" but that regvar does not exist in the data row.`);
        }
        regvarsMap[shortname] = createRegvarModel(regvars[shortname], shortname, regvarMetadata);
    });
    forOwn(tableMetadata.vdlists, (vdMetadata, key)=>{
        if (!row.vdlists[key]) {
            throw new Error(`Table metadata for "${tn}" contains a vdlist "${key}" but that vdlist does not exist in the data row.`);
        }
        const { value, include, ...vdExtra } = vdlists[key];
        vdlistsMap[key] = new ValueDomainModel({
            value: value,
            include: include,
            $extra: vdExtra,
            $rcc: {
                vdlist: key,
                regvarMetadata: vdMetadata
            }
        });
    });
    tableMetadata.subTables.forEach((subTableName)=>{
        if (!row.subTables[subTableName]) {
            throw new Error(`Table metadata for "${tn}" contains a subtable "${subTableName}" but that subtable does not exist in the data row.`);
        }
        const rows = subTables[subTableName];
        const rowModels = rows.map((r)=>createViewModel(r, metadata, subTableName));
        subTablesMap[subTableName] = new SubTableModel({
            items: ImmutableList(rowModels),
            $rcc: {
                tableName: subTableName,
                tableMetadata: metadata[subTableName],
                globalMetadata: metadata
            }
        });
    });
    return new RowModel({
        id: id,
        _destroy: _destroy,
        $id: id || uniqueId('data_'),
        $extra: rowExtra,
        regvars: ImmutableMap(regvarsMap),
        vdlists: ImmutableMap(vdlistsMap),
        subTables: ImmutableMap(subTablesMap),
        $rcc: {
            tableName: tn,
            tableMetadata: tableMetadata,
            globalMetadata: metadata
        }
    });
}
/**
 * Creates the initial data state required by designedForm. The store supports undo and redo.
 * @param {Object} [row] - INCA data to create store for. If missing inca.form.data is used.
 * @param {Object} [globalMetadata] - INCA metadata object. If missing inca.form.metadata is used.
 * @param {string} [tableName] - Table name representing the passed row. If missing the top-level table in inca.form.metadata is used.
 *
 * @returns {{past: Array, present: RowModel, future: Array}}
 */ export function createDataState(row, globalMetadata, tableName) {
    return {
        past: [],
        present: createViewModel(row || incaAccessor().form.data, globalMetadata, tableName),
        future: []
    };
}
export function createViewModelRow(tableName) {
    const inca = incaAccessor();
    if (!inca || !inca.form || !inca.form.metadata) {
        throw new Error('inca.form.metadata does not exist.');
    }
    const row = createDataRow(tableName);
    return createViewModel(row, inca.form.metadata, tableName);
}
export function getActiveRows(rows) {
    const activeRows = rows.filterNot((x)=>x.get('_destroy'));
    // Only return the new immutable collection if there is any difference
    if (activeRows.count() !== rows.count()) {
        return activeRows;
    }
    return rows;
}
